import { useMutation, useQueryClient } from '@tanstack/react-query';
import pluralize from 'pluralize';

import { parseApiError } from '@cast/api-client';
import { notify } from '@cast/design-system';
import { apiClient } from '@cast/infrastructure-api';
import { UpdateCommitmentRequest } from '@cast/types';

import { getCommitmentsAssignmentsListQueryKey } from 'hooks/queries/commitments/useAssignmentsQuery';
import { getCommitmentAssignmentsQueryKey } from 'hooks/queries/commitments/useCommitmentAssignmentsQuery';
import { getCommitmentsQueryKey } from 'hooks/queries/commitments/useCommitmentsQuery';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

export const useUpdateCommitmentsMutation = () => {
  const queryClient = useQueryClient();
  const { currentOrganization } = useOrganizations();

  return useMutation({
    mutationFn: async (requests: UpdateCommitmentRequest[]) => {
      if (requests.length > 1) {
        return await apiClient.commitments.batchUpdateCommitments({
          organizationId: currentOrganization!.id,
          body: {
            requests,
          },
        });
      }
      return await apiClient.commitments.updateCommitment({
        commitmentId: requests[0].commitmentId,
        commitment: requests[0].commitment!,
      });
    },
    onSuccess: (_, requests) => {
      queryClient.invalidateQueries({
        queryKey: getCommitmentsQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getCommitmentsAssignmentsListQueryKey(),
      });
      queryClient.invalidateQueries({
        queryKey: getCommitmentAssignmentsQueryKey(),
      });
      notify.success(`${pluralize('Commitment', requests.length)} updated`);
    },
    onError: (error, requests) => {
      notify.error(
        `Failed to update ${pluralize(
          'commitment',
          requests.length
        )}: ${parseApiError(error)}`
      );
    },
  });
};
